import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, CalendarDays, LayoutDashboard, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { StepCard } from "../step-card";
import { motionSafe, staggerContainer, SPRING } from "../motion";

/** Same booking page /book-demo redirects to. */
const BOOK_ASH_URL = "https://startupfuel.zohobookings.com/#/dgpt";

/**
 * A short burst of particles on completion.
 *
 * Hand-rolled rather than pulling in a confetti library — a dozen absolutely
 * positioned divs on a spring is a few lines, has no bundle cost, and stops on
 * its own instead of running a canvas loop.
 */
function Burst() {
  const shouldReduce = useReducedMotion();
  if (shouldReduce) return null;

  const colours = ["#3B82F6", "#F59E0B", "#EC4899", "#8B5CF6", "#10B981", "#6366F1"];

  return (
    <div className="pointer-events-none absolute left-1/2 top-1/2 h-0 w-0">
      {Array.from({ length: 14 }).map((_, i) => {
        // Fixed geometry, not randomised — the pattern reads as designed
        // rather than noisy, and it renders identically every time.
        const angle = (i / 14) * Math.PI * 2;
        const distance = 70 + (i % 3) * 22;

        return (
          <motion.span
            key={i}
            className="absolute h-1.5 w-1.5 rounded-full"
            style={{ backgroundColor: colours[i % colours.length] }}
            initial={{ x: 0, y: 0, scale: 0, opacity: 1 }}
            animate={{
              x: Math.cos(angle) * distance,
              y: Math.sin(angle) * distance,
              scale: [0, 1, 0.4],
              opacity: [1, 1, 0],
            }}
            transition={{ duration: 1, delay: 0.1 + (i % 5) * 0.03, ease: "easeOut" }}
          />
        );
      })}
    </div>
  );
}

export function FinishStep({
  dealName,
  onGoToDeal,
  onGoToDashboard,
}: {
  dealName?: string;
  onGoToDeal?: () => void;
  onGoToDashboard: () => void;
}) {
  const shouldReduce = useReducedMotion();

  return (
    <StepCard className="relative overflow-visible">
      <Burst />

      <motion.div
        className="relative space-y-4"
        variants={motionSafe(staggerContainer, shouldReduce)}
        initial="hidden"
        animate="visible"
      >
        <div className="flex items-start gap-3">
          <motion.div
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary"
            initial={{ scale: 0, rotate: -45 }}
            animate={{ scale: 1, rotate: 0 }}
            transition={{ ...SPRING, delay: 0.05 }}
          >
            <Sparkles className="h-4.5 w-4.5" />
          </motion.div>
          <div className="pt-1">
            <p className="text-sm font-semibold text-foreground">
              Your workspace is live.
            </p>
            <p className="mt-0.5 text-sm text-muted-foreground">
              {dealName
                ? `${dealName} is being analysed now — the report lands in a few minutes.`
                : "Everything's set up and ready to go."}
            </p>
            <p className="mt-1.5 text-sm text-muted-foreground">
              Want a walkthrough? Book time with Ash and we'll tailor it to how you work.
            </p>
          </div>
        </div>

        <div className="flex flex-col gap-2 sm:flex-row sm:justify-end">
          <Button variant="ghost" asChild className="gap-2 sm:mr-auto">
            <a href={BOOK_ASH_URL} target="_blank" rel="noopener noreferrer">
              <CalendarDays className="h-4 w-4" />
              Book time with Ash
            </a>
          </Button>
          <Button variant="outline" onClick={onGoToDashboard} className="gap-2">
            <LayoutDashboard className="h-4 w-4" />
            Go to dashboard
          </Button>
          {onGoToDeal && (
            <Button onClick={onGoToDeal} className="gap-2">
              {dealName ? `Open ${dealName}` : "Open deal"}
              <ArrowRight className="h-4 w-4" />
            </Button>
          )}
        </div>
      </motion.div>
    </StepCard>
  );
}
